/**
 * Google Analytics 4 mit Google Consent Mode v2.
 *
 * Vorgeschichte, damit niemand die Konstruktion versehentlich zurückbaut:
 * v1 hat gtag über `layouts/partials/_analytics.haml` unbedingt im <head>
 * geladen — jeder Besucher wurde gezählt. v2 hat bis 09.2026 gar nicht
 * gemessen und danach gtag erst NACH einem Klick auf "Zustimmen" geladen.
 * Dieselbe GA-Property, zwei unvereinbare Zählweisen: die Statistik meldete
 * rund 16 Nutzer pro Tag, während die nginx-Logs über 80.000 eindeutige
 * Besucher zeigten. Der gemeldete "Einbruch" nach dem Relaunch war komplett
 * ein Messfehler.
 *
 * Die Lösung ist Consent Mode v2 statt eines harten Ladegatters:
 *
 * - `initAnalytics()` läuft bei jedem Seitenaufbau, unabhängig von der
 *   Einwilligung. Noch VOR `config` setzt es alle Consent-Typen auf `denied`.
 *   gtag speichert dann nichts auf dem Gerät und sendet nur cookielose,
 *   nicht personenbezogene Pings — Reichweite wird wieder vollständig
 *   gezählt, ohne Einwilligung vorauszusetzen.
 * - Erst `grantAnalyticsConsent()` hebt den Status per `consent update` auf
 *   `granted` an; ab dann setzt GA seine Cookies.
 *
 * Der frühere Einwand ("ein geladenes GA-Skript setzt seine Cookies sofort")
 * greift genau deshalb nicht mehr: mit `default: denied` vor dem `config`
 * unterbleibt jeder Speicherzugriff, bis zugestimmt wurde.
 *
 * Widerruf: `revokeAnalyticsConsent()` setzt den Status zurück auf `denied`
 * und löscht die bereits gesetzten _ga-Cookies.
 */

import { loadScriptOnce } from './loadScriptOnce'

export const GA_MEASUREMENT_ID = 'G-KE09R37PNY'
export const CONSENT_STORAGE_KEY = 'ht-analytics-consent'

/** Google-Ads-Conversion aus v1 (`dashboard/index.haml`). */
export const ADS_CONVERSION_SEND_TO = 'AW-1006291848/XdX_CPjK-O8CEIiX698D'

/** Die vier Consent-Typen, die Consent Mode v2 kennt. */
const CONSENT_TYPES = [
  'ad_storage',
  'ad_user_data',
  'ad_personalization',
  'analytics_storage',
] as const

function consentPayload(value: 'granted' | 'denied'): Record<string, string> {
  return Object.fromEntries(CONSENT_TYPES.map((type) => [type, value]))
}

export type ConsentState = 'granted' | 'denied' | 'unknown'

declare global {
  interface Window {
    dataLayer?: unknown[]
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gtag?: (...args: any[]) => void
  }
}

/** Gespeicherte Entscheidung. `unknown` heisst: noch nicht gefragt. */
export function getConsent(): ConsentState {
  try {
    const value = localStorage.getItem(CONSENT_STORAGE_KEY)
    return value === 'granted' || value === 'denied' ? value : 'unknown'
  } catch {
    // Privater Modus oder blockierter Speicher: lieber nicht messen, als ohne
    // nachweisbare Einwilligung zu messen.
    return 'denied'
  }
}

function persistConsent(state: Exclude<ConsentState, 'unknown'>): void {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, state)
  } catch {
    // Ohne Speicher gilt die Entscheidung nur fuer diese Sitzung.
  }
}

let loaded = false

/**
 * Bindet gtag.js ein — einmal pro Seitenaufbau, unabhängig von der
 * Einwilligung. Der Consent-Status wird vor `config` gesetzt, damit ohne
 * Zustimmung nichts gespeichert wird.
 */
export function initAnalytics(): void {
  if (loaded || typeof document === 'undefined') return
  // In Entwicklung und Tests wuerde die Messung nur die Zahlen verfaelschen.
  if (!import.meta.env.PROD) return

  loaded = true

  window.dataLayer = window.dataLayer || []
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments)
  }

  // Reihenfolge ist hier nicht kosmetisch: `consent default` MUSS vor dem
  // `config` im dataLayer stehen, sonst wertet gtag den ersten Treffer noch
  // mit Speicherzugriff aus. `wait_for_update` gibt dem Banner ein halbe
  // Sekunde, eine bereits getroffene Entscheidung nachzureichen.
  window.gtag('consent', 'default', {
    ...consentPayload(getConsent() === 'granted' ? 'granted' : 'denied'),
    wait_for_update: 500,
  })
  window.gtag('js', new Date())
  // IP-Kuerzung ist bei GA4 Standard; `anonymize_ip` explizit zu setzen schadet
  // nicht und dokumentiert die Absicht an der Stelle, an der jemand nachsieht.
  window.gtag('config', GA_MEASUREMENT_ID, { anonymize_ip: true })

  loadScriptOnce(`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`)
}

function updateConsent(value: 'granted' | 'denied'): void {
  window.gtag?.('consent', 'update', consentPayload(value))
}

export function grantAnalyticsConsent(): void {
  persistConsent('granted')
  updateConsent('granted')
}

export function denyAnalyticsConsent(): void {
  persistConsent('denied')
  updateConsent('denied')
}

/** Widerruf: Entscheidung zuruecksetzen und GA-Cookies loeschen. */
export function revokeAnalyticsConsent(): void {
  persistConsent('denied')
  updateConsent('denied')
  if (typeof document === 'undefined') return
  document.cookie
    .split(';')
    .map((c) => c.split('=')[0]?.trim())
    .filter((name): name is string => !!name && (name.startsWith('_ga') || name === '_gid'))
    .forEach((name) => {
      // Ohne passende Domain trifft das Loeschen das Cookie nicht — GA setzt
      // auf der registrierbaren Domain, nicht auf dem vollen Hostnamen.
      const host = window.location.hostname
      const registrable = host.split('.').slice(-2).join('.')
      document.cookie = `${name}=; Max-Age=0; path=/`
      document.cookie = `${name}=; Max-Age=0; path=/; domain=.${registrable}`
    })
}

/**
 * Seitenaufruf melden. Hängt bewusst nur noch an `window.gtag` und nicht mehr
 * an der Einwilligung — ohne Zustimmung sendet gtag den Treffer cookielos.
 */
export function trackPageView(path: string): void {
  window.gtag?.('event', 'page_view', { page_path: path })
}

/**
 * Google-Ads-Conversion melden. In v1 lag der Aufruf als nacktes Inline-Skript
 * auf der Dashboard-Seite und warf einen ReferenceError, sobald der
 * Analytics-Partial nicht gerendert wurde (Development, Premium-Zweig).
 * Der optionale Aufruf hier kann das nicht.
 */
export function trackAdsConversion(): void {
  window.gtag?.('event', 'conversion', { send_to: ADS_CONVERSION_SEND_TO })
}

/**
 * Abgeschlossener Kauf als GA4-Standardereignis.
 *
 * Bewusst NICHT über `ADS_CONVERSION_SEND_TO`: dieses Label steht in Google
 * Ads seit v1 für "Dashboard aufgerufen". Beide Ereignisse auf dasselbe Label
 * zu legen würde echte Käufe mit Dashboard-Aufrufen vermischen und die
 * Kampagnenauswertung unbrauchbar machen. Aus diesem `purchase`-Ereignis lässt
 * sich in Google Ads eine eigene, saubere Conversion-Aktion anlegen.
 */
export function trackPurchase(params: {
  planId: string
  value: number
  currency: string
  termMonths: number
}): void {
  window.gtag?.('event', 'purchase', {
    value: params.value,
    currency: params.currency,
    items: [{ item_id: params.planId, quantity: params.termMonths }],
  })
}
